import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { confirm } from '../components/ui/confirm-dialog';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [roleCapabilities, setRoleCapabilities] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [showIntro, setShowIntro] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  // Catalog of every permission codename, used to decide whether a gate applies at all.
  const [globalPermissions, setGlobalPermissions] = useState([]);

  // Check for existing session on app load
  const checkExistingSession = useCallback(async () => {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const userData = localStorage.getItem('userData');
      
      if (sessionToken && userData) {
        // Verify session with backend
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${API_URL}/users/login/auth/verify/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setUser(data.data.user);
            // No server-side session; preserve token from localStorage
            const sessionToken = localStorage.getItem('sessionToken');
            setSession(sessionToken ? { token: sessionToken } : null);
            setIsAuthenticated(true);
            // Hydrate restaurantId for RestaurantContext, in case an older login never set it.
            if (data.data.user?.restaurant_id && !localStorage.getItem('restaurantId')) {
              localStorage.setItem('restaurantId', String(data.data.user.restaurant_id));
            }
            // If user was mid-onboarding when they refreshed, keep them there
            if (sessionStorage.getItem('startOnboarding') === '1') {
              setNeedsOnboarding(true);
            }
            await refreshPermissions(data.data.user);
          } else {
            // Session invalid, clear stored data
            clearAuthData();
          }
        } else {
          // Session invalid, clear stored data
          clearAuthData();
        }
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkExistingSession();
  }, [checkExistingSession]);

  const login = (userData, sessionData) => {
    const restaurantId = sessionData?.restaurantId || userData?.restaurant_id || userData?.restaurant?.id || userData?.restaurant || null;
    const normalizedUser = restaurantId && !userData?.restaurant_id
      ? { ...userData, restaurant_id: restaurantId }
      : userData;

    setUser(normalizedUser);
    setSession(sessionData);
    setIsAuthenticated(true);

    // Detect first-time onboarding: set by the login screen's "first time" button
    const wantsOnboarding = sessionStorage.getItem('startOnboarding') === '1';
    if (wantsOnboarding) {
      setNeedsOnboarding(true);
    } else {
      setShowIntro(true);
    }

    refreshPermissions(normalizedUser);

    // Store in localStorage
    localStorage.setItem('sessionToken', sessionData.token);
    localStorage.setItem('userData', JSON.stringify(normalizedUser));
    // Persist the user's restaurant so RestaurantContext can hydrate on reload
    if (restaurantId) {
      localStorage.setItem('restaurantId', String(restaurantId));
    }
  };

  const dismissIntro = () => setShowIntro(false);

  const cancelOnboarding = async () => {
    // Silent return to login: end the demo session and clear state — no confirm prompt
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const userData = localStorage.getItem('userData');
      if (sessionToken && userData) {
        const u = JSON.parse(userData);
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        await fetch(`${API_URL}/users/session/end/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({ user_id: u.id }),
        }).catch(() => {});
        await fetch(`${API_URL}/users/login/auth/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({ session_token: sessionToken }),
        }).catch(() => {});
      }
    } finally {
      sessionStorage.removeItem('startOnboarding');
      setNeedsOnboarding(false);
      clearAuthData();
    }
  };

  const completeOnboarding = (newUser, newSession) => {
    sessionStorage.removeItem('startOnboarding');
    setNeedsOnboarding(false);
    if (newUser && newSession) {
      // swap the authenticated identity to the freshly-created user
      setUser(newUser);
      setSession(newSession);
      localStorage.setItem('sessionToken', newSession.token);
      localStorage.setItem('userData', JSON.stringify(newUser));
      refreshPermissions(newUser);
    }
    setShowIntro(true);
  };

  const logout = async () => {
    // Confirm logout
    if (!(await confirm({ title: 'Log out?', description: `Are you sure you want to log out?`, confirmText: 'Log out', tone: 'destructive' }))) {
      return;
    }
    
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const userData = localStorage.getItem('userData');
      
      if (sessionToken && userData) {
        const user = JSON.parse(userData);
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        
        // End session
        await fetch(`${API_URL}/users/session/end/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ user_id: user.id })
        });
        
        // Call logout API
        await fetch(`${API_URL}/users/login/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ session_token: sessionToken })
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local state regardless of API call success
      clearAuthData();
    }
  };

  const clearAuthData = () => {
    setUser(null);
    setSession(null);
    setIsAuthenticated(false);
    setRole(null);
    setRoleCapabilities(null);
    setPermissions([]);
    setGlobalPermissions([]);
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('userData');
  };

  // Refresh role and permissions for current user
  const refreshPermissions = async (userData) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const sessionToken = localStorage.getItem('sessionToken');
      const headers = sessionToken ? {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json'
      } : { 'Content-Type': 'application/json' };

      // 1) Get role + permissions for the current user
      let roleBasic = null;
      let perms = [];
      const roleResp = await fetch(`${API_URL}/users/login/roles/user/${userData.id}/`, { headers });
      if (roleResp.ok) {
        const roleJson = await roleResp.json();
        roleBasic = roleJson?.data?.role || null;
        perms = roleJson?.data?.permissions || [];
      }

      // 2) Map to capabilities via roles list
      let caps = null;
      const listResp = await fetch(`${API_URL}/users/login/roles/`, { headers });
      if (listResp.ok) {
        const listJson = await listResp.json();
        const match = (listJson.data?.roles || []).find(x => x.id === roleBasic?.id);
        caps = match?.capabilities || null;
        // Prefer permissions from the roles list if richer (id+codename+name) — they're the same source either way
        if (match?.permissions?.length) {
          perms = match.permissions;
        }
      }

      setRole(roleBasic);
      setRoleCapabilities(caps);
      setPermissions(perms);

      // Refresh the global permission catalog too. Failures are non-fatal.
      try {
        const allResp = await fetch(`${API_URL}/users/login/permissions/`, { headers });
        if (allResp.ok) {
          const allJson = await allResp.json();
          setGlobalPermissions(allJson?.data || []);
        }
      } catch (_) { /* ignore */ }
    } catch (e) {
      // ignore
    }
  };

  // True when the codename exists in the catalog; when false the caller should treat the gate as open.
  const isPermissionGated = (codename) =>
    !!globalPermissions.find((p) => p.codename === codename);

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('userData', JSON.stringify(updatedUserData));
  };

  const getAuthHeaders = () => {
    const sessionToken = localStorage.getItem('sessionToken');
    return sessionToken ? {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    const headers = {
      ...getAuthHeaders(),
      ...options.headers
    };
    
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    // If unauthorized, clear auth data and redirect to login
    if (response.status === 401) {
      clearAuthData();
      return response;
    }
    
    return response;
  };

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated,
    role,
    // Super admin is the 'Super Admin' role or a central superuser; such users may switch the active company.
    isSuperAdmin: role?.name === 'Super Admin' || !!user?.is_superuser,
    roleCapabilities,
    permissions,
    globalPermissions,
    isPermissionGated,
    showIntro,
    dismissIntro,
    needsOnboarding,
    completeOnboarding,
    cancelOnboarding,
    hasPermission: (code) => !!permissions.find(p => p.codename === code || p.name === code),
    // Capability gate that fails OPEN when the answer is unknown; only an explicit false denies.
    hasCapability: (cap) =>
      role?.name === 'Owner' || role?.name === 'Super Admin' || !!user?.is_superuser
        ? true
        : roleCapabilities == null ? true : !!roleCapabilities[cap],
    login,
    logout,
    updateUser,
    getAuthHeaders,
    makeAuthenticatedRequest,
    checkExistingSession,
    refreshPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
